import React from 'react';
import styled from 'styled-components';
import { Plus, Trash2 } from 'lucide-react';
import { Button, Card, CardHead, INK, Input, Muted } from '../review/reviewStyles';
import { businessLabel } from '../../presentation/contractPresentation';
import {
  COVENANT_FORMULAS, ELIGIBILITY_FIELDS, REPORTING_FREQUENCIES, termDraft,
  type ManualAuthorityDraft, type ManualCovenantDraft, type ManualEligibilityRuleDraft,
} from '../ManualAuthorityEditor';
import { NOT_STATED, isNotStated, type EntryField, type IdentityDraft } from './entryModel';

/**
 * The entry editors, one section at a time, on the contract review palette
 * (standard approved 2026-09-11). Each contract value sits above the clause it
 * comes from, at the same reading size as the review page — not the 0.6rem
 * reference-data grid this replaced.
 */

const Fields = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${p => p.$columns || 2}, minmax(0, 1fr));
  gap: 0.85rem;
  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 0.3rem;

  > label { color: ${INK.soft}; font-size: 0.8rem; font-weight: 650; }
  .hint { color: ${INK.faint}; font-size: 0.72rem; }
  .clause-label { color: ${INK.faint}; font-size: 0.7rem; font-weight: 600; margin-top: 0.15rem; }
  .silent { display: flex; align-items: center; gap: 0.4rem; margin-top: 0.1rem;
    color: ${INK.faint}; font-size: 0.74rem; font-weight: 600; cursor: pointer; }
  .silent input { min-height: auto; }
  .silent.on { color: ${INK.accentSoft}; }
  select {
    box-sizing: border-box; width: 100%; min-width: 0; padding: 0.5rem 0.6rem;
    border: 1px solid ${INK.lineStrong}; border-radius: 8px; background: ${INK.well};
    color: ${INK.body}; color-scheme: dark; font: inherit; font-size: 0.86rem; min-height: 40px;
  }
  select:focus { outline: none; border-color: ${INK.accent}; box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.18); }
  input { font-size: 0.86rem; min-height: 40px; }
  input.clause { font-size: 0.78rem; min-height: 34px; background: rgba(2, 6, 23, 0.6); color: ${INK.muted}; }
  input.clause:focus { color: ${INK.body}; }
`;

const Repeated = styled.div`
  margin-top: 0.85rem;
  padding: 0.85rem;
  border: 1px solid ${INK.line};
  border-radius: 10px;
  background: ${INK.raised};

  .repeated-head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 0.75rem; margin-bottom: 0.75rem;
  }
  .repeated-head h3 { margin: 0; color: ${INK.strong}; font-size: 0.88rem; }
`;

const Attest = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-top: 0.9rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid ${INK.line};
  border-radius: 10px;
  background: ${INK.raised};
  color: ${INK.soft};
  font-size: 0.84rem;
  cursor: pointer;

  input { margin-top: 0.15rem; }
  span small { display: block; color: ${INK.faint}; font-size: 0.74rem; margin-top: 0.2rem; }
`;

const Empty = styled.p`
  margin: 0.85rem 0 0;
  color: ${INK.faint};
  font-size: 0.82rem;
`;

/* ── Name and references ─────────────────────────────────────────────── */

export const IdentityEditor: React.FC<{
  value: IdentityDraft;
  onChange: (value: IdentityDraft) => void;
}> = ({ value, onChange }) => <Card>
  <CardHead>
    <div>
      <h2>Name and references</h2>
      <p>Your own references. A checker does not verify these against a clause, so they carry none.</p>
    </div>
  </CardHead>
  <Fields>
    <Field>
      <label htmlFor="entry-facility-name">Facility name</label>
      <Input id="entry-facility-name" value={value.facilityName} autoComplete="off"
        placeholder="Cascade Equipment Finance Warehouse 2026-1"
        onChange={event => onChange({ ...value, facilityName: event.target.value })} />
      <span className="hint">Required. How the facility appears everywhere in the platform.</span>
    </Field>
    <Field>
      <label htmlFor="entry-facility-code">Facility code</label>
      <Input id="entry-facility-code" value={value.facilityCode} autoComplete="off"
        placeholder="ABF-CEF-2026-1"
        onChange={event => onChange({ ...value, facilityCode: event.target.value })} />
      <span className="hint">Your lender reference. Left blank, the platform assigns one.</span>
    </Field>
    <Field>
      <label htmlFor="entry-external-id">External reference</label>
      <Input id="entry-external-id" value={value.externalId} autoComplete="off"
        placeholder="CEF-WHSE-2026-1"
        onChange={event => onChange({ ...value, externalId: event.target.value })} />
      <span className="hint">The identifier in your system of record, if you reconcile against one.</span>
    </Field>
  </Fields>
</Card>;

/* ── The agreement itself ────────────────────────────────────────────── */

export const AuthorityEditor: React.FC<{
  value: ManualAuthorityDraft;
  onChange: (value: ManualAuthorityDraft) => void;
}> = ({ value, onChange }) => <Card>
  <CardHead>
    <div>
      <h2>The agreement itself</h2>
      <p>Everything entered on this facility is recorded as asserted under this document. The checker sees it beside every value.</p>
    </div>
  </CardHead>
  <Fields>
    <Field>
      <label htmlFor="entry-authority-reference">Executed agreement</label>
      <Input id="entry-authority-reference" value={value.authorityReference} autoComplete="off"
        placeholder="Cascade Equipment Finance Warehouse Credit Agreement dated September 4, 2026"
        onChange={event => onChange({ ...value, authorityReference: event.target.value })} />
      <span className="hint">Required. Name and date the document as it identifies itself.</span>
    </Field>
    <Field>
      <label htmlFor="entry-terms-clause">Clause governing the facility terms</label>
      <Input id="entry-terms-clause" value={value.termSourceClause} autoComplete="off"
        placeholder="Article II"
        onChange={event => onChange({ ...value, termSourceClause: event.target.value })} />
      <span className="hint">Required. The fallback citation for any term you do not cite individually.</span>
    </Field>
    <Field>
      <label htmlFor="entry-effective-from">Contract effective date</label>
      <Input id="entry-effective-from" value={value.effectiveFrom} inputMode="numeric" autoComplete="off"
        placeholder="09/04/2026"
        onChange={event => onChange({ ...value, effectiveFrom: event.target.value })} />
      <span className="hint">Required. MM/DD/YYYY or YYYY-MM-DD. Collection periods are generated from it.</span>
    </Field>
  </Fields>
</Card>;

/* ── Cited contract terms ────────────────────────────────────────────── */

export const TermsEditor: React.FC<{
  title: string;
  purpose: string;
  fields: EntryField[];
  value: ManualAuthorityDraft;
  onChange: (value: ManualAuthorityDraft) => void;
  fallbackClause: string;
}> = ({ title, purpose, fields, value, onChange, fallbackClause }) => {
  const update = (key: string, patch: { value?: string; sourceClause?: string }) => onChange({
    ...value,
    terms: { ...(value.terms || {}), [key]: { ...termDraft(value, key), ...patch } },
  });
  return <Card>
    <CardHead><div><h2>{title}</h2><p>{purpose}</p></div></CardHead>
    <Fields>
      {fields.map(entry => {
        const draft = termDraft(value, entry.key);
        const label = businessLabel(entry.key);
        const silent = isNotStated(draft.value);
        return <Field key={entry.key}>
          <label htmlFor={`entry-term-${entry.key}`}>
            {label}
            {entry.required && <Muted> · required</Muted>}
            {entry.material && !entry.required && <Muted> · material</Muted>}
          </label>
          {entry.kind === 'select'
            ? <select id={`entry-term-${entry.key}`} value={silent ? '' : draft.value} disabled={silent}
                onChange={event => update(entry.key, { value: event.target.value })}>
                <option value="">Not entered</option>
                {(entry.options || []).map(option => <option key={option} value={option}>{option}</option>)}
              </select>
            : <Input id={`entry-term-${entry.key}`}
                type={!silent && (entry.kind === 'number' || entry.kind === 'integer') ? 'number' : 'text'}
                step={entry.kind === 'integer' ? '1' : entry.kind === 'number' ? 'any' : undefined}
                inputMode={entry.kind === 'date' ? 'numeric' : undefined}
                value={silent ? 'Not stated in the agreement' : draft.value}
                disabled={silent} autoComplete="off"
                placeholder={entry.kind === 'date' ? 'MM/DD/YYYY' : entry.hint || ''}
                onChange={event => update(entry.key, { value: event.target.value })} />}
          {entry.hint && entry.kind !== 'date' && !silent && <span className="hint">{entry.hint}</span>}
          {/* A material term the agreement is silent on must be decided, not left
              blank: unanswered keeps the contract incomplete and the save refused. */}
          {entry.material && !entry.required && <label className={`silent${silent ? ' on' : ''}`}>
            <input type="checkbox" checked={silent}
              onChange={event => update(entry.key, { value: event.target.checked ? NOT_STATED : '' })} />
            The agreement does not state this
          </label>}
          <span className="clause-label" id={`entry-clause-label-${entry.key}`}>
            {silent ? 'Clause searched' : 'Clause for this value'}
          </span>
          <Input className="clause" value={draft.sourceClause} autoComplete="off"
            aria-labelledby={`entry-clause-label-${entry.key}`}
            placeholder={fallbackClause ? `Defaults to ${fallbackClause}` : 'Section 2.01'}
            onChange={event => update(entry.key, { sourceClause: event.target.value })} />
        </Field>;
      })}
    </Fields>
  </Card>;
};

/* ── Eligibility rules ───────────────────────────────────────────────── */

const newRule = (): ManualEligibilityRuleDraft => ({
  ruleName: '', ruleType: 'ROW', fieldName: '', operator: 'LTE',
  thresholdValue: '', thresholdText: '', advanceRatePct: '', sourceClause: '',
});

export const RulesEditor: React.FC<{
  value: ManualAuthorityDraft;
  onChange: (value: ManualAuthorityDraft) => void;
}> = ({ value, onChange }) => {
  const update = (index: number, patch: Partial<ManualEligibilityRuleDraft>) => onChange({
    ...value,
    eligibilityRules: value.eligibilityRules.map((rule, i) => i === index ? { ...rule, ...patch } : rule),
  });
  return <Card>
    <CardHead>
      <div>
        <h2>Eligibility rules</h2>
        <p>Each rule tests one canonical field on the servicer tape. A numeric field takes a numeric threshold, a text or boolean field takes EQ or NE — the borrowing base cannot apply a rule it cannot evaluate.</p>
      </div>
      <Button type="button" onClick={() => onChange({ ...value, eligibilityRules: [...value.eligibilityRules, newRule()] })}>
        <Plus size={13} /> Add a rule
      </Button>
    </CardHead>
    {!value.eligibilityRules.length && <Empty>No rules yet. At least one row-level rule is required before activation.</Empty>}
    {value.eligibilityRules.map((rule, index) => <Repeated key={`rule-${index}`}>
      <div className="repeated-head">
        <h3>{rule.ruleName.trim() || `Rule ${index + 1}`}</h3>
        <Button type="button" $variant="danger"
          onClick={() => onChange({ ...value, eligibilityRules: value.eligibilityRules.filter((_, i) => i !== index) })}>
          <Trash2 size={13} /> Remove
        </Button>
      </div>
      <Fields>
        <Field>
          <label htmlFor={`rule-name-${index}`}>Rule name</label>
          <Input id={`rule-name-${index}`} value={rule.ruleName} autoComplete="off"
            placeholder="No receivable more than 60 days past due"
            onChange={event => update(index, { ruleName: event.target.value })} />
        </Field>
        <Field>
          <label htmlFor={`rule-type-${index}`}>What it limits</label>
          <select id={`rule-type-${index}`} value={rule.ruleType}
            onChange={event => update(index, { ruleType: event.target.value as ManualEligibilityRuleDraft['ruleType'] })}>
            <option value="ROW">Each asset must pass (ROW)</option>
            <option value="ROW_OPTIONAL">Each asset, where reported (ROW_OPTIONAL)</option>
            <option value="ADVANCE_RATE">Applies an advance rate (ADVANCE_RATE)</option>
            <option value="CONCENTRATION_OBLIGOR">Obligor concentration cap</option>
            <option value="CONCENTRATION_STATE">State concentration cap</option>
            <option value="CONCENTRATION_VINTAGE">Vintage concentration cap</option>
            <option value="CONCENTRATION_COUNTRY">Country concentration cap</option>
          </select>
        </Field>
        <Field>
          <label htmlFor={`rule-field-${index}`}>Tape field</label>
          <select id={`rule-field-${index}`} value={rule.fieldName}
            onChange={event => update(index, { fieldName: event.target.value })}>
            <option value="">Select…</option>
            {ELIGIBILITY_FIELDS.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
        </Field>
        <Field>
          <label htmlFor={`rule-operator-${index}`}>Test</label>
          <select id={`rule-operator-${index}`} value={rule.operator}
            onChange={event => update(index, { operator: event.target.value })}>
            {['LTE', 'LT', 'GTE', 'GT', 'EQ', 'NE'].map(op => <option key={op} value={op}>{op}</option>)}
          </select>
        </Field>
        <Field>
          <label htmlFor={`rule-threshold-${index}`}>Numeric threshold</label>
          <Input id={`rule-threshold-${index}`} type="number" step="any" value={rule.thresholdValue}
            onChange={event => update(index, { thresholdValue: event.target.value, thresholdText: '' })} />
          <span className="hint">For a numeric tape field. A concentration cap is a 0-100 percentage.</span>
        </Field>
        <Field>
          <label htmlFor={`rule-text-${index}`}>Text threshold</label>
          <Input id={`rule-text-${index}`} value={rule.thresholdText} autoComplete="off"
            onChange={event => update(index, { thresholdText: event.target.value, thresholdValue: '' })} />
          <span className="hint">For a text or boolean field, with EQ or NE. Use one threshold, not both.</span>
        </Field>
        {rule.ruleType === 'ADVANCE_RATE' && <Field>
          <label htmlFor={`rule-rate-${index}`}>Applied advance rate (%)</label>
          <Input id={`rule-rate-${index}`} type="number" step="any" value={rule.advanceRatePct}
            onChange={event => update(index, { advanceRatePct: event.target.value })} />
        </Field>}
        <Field>
          <label htmlFor={`rule-clause-${index}`}>Clause</label>
          <Input id={`rule-clause-${index}`} value={rule.sourceClause} autoComplete="off"
            placeholder='Definition of "Eligible Receivable", clause (f)'
            onChange={event => update(index, { sourceClause: event.target.value })} />
          <span className="hint">Required. A rule without its clause cannot be verified.</span>
        </Field>
      </Fields>
    </Repeated>)}
  </Card>;
};

/* ── Covenants ───────────────────────────────────────────────────────── */

const newCovenant = (): ManualCovenantDraft => ({
  covenantKey: '', covenantName: '', thresholdType: 'MIN', thresholdValue: '',
  formulaExpression: '', reportingFrequency: '', sourceClause: '',
});

export const CovenantsEditor: React.FC<{
  value: ManualAuthorityDraft;
  onChange: (value: ManualAuthorityDraft) => void;
}> = ({ value, onChange }) => {
  const update = (index: number, patch: Partial<ManualCovenantDraft>) => onChange({
    ...value,
    covenants: value.covenants.map((covenant, i) => i === index ? { ...covenant, ...patch } : covenant),
  });
  return <Card>
    <CardHead>
      <div>
        <h2>Financial covenants</h2>
        <p>Only formulas the platform computes are offered. A covenant whose formula is not here cannot be tested, and recording it would promise monitoring that never runs.</p>
      </div>
      {!value.noFinancialCovenants && <Button type="button"
        onClick={() => onChange({ ...value, covenants: [...value.covenants, newCovenant()] })}>
        <Plus size={13} /> Add a covenant
      </Button>}
    </CardHead>

    <Attest>
      <input type="checkbox" checked={value.noFinancialCovenants}
        onChange={event => onChange({ ...value, noFinancialCovenants: event.target.checked })} />
      <span>
        This agreement has no financial covenants to monitor
        <small>Recorded as your attestation under the named agreement. The checker sees it as a decision, not as an empty section.</small>
      </span>
    </Attest>

    {!value.noFinancialCovenants && !value.covenants.length
      && <Empty>No covenants yet. Enter them, or attest above that the agreement has none.</Empty>}

    {!value.noFinancialCovenants && value.covenants.map((covenant, index) => <Repeated key={`covenant-${index}`}>
      <div className="repeated-head">
        <h3>{covenant.covenantName.trim() || `Covenant ${index + 1}`}</h3>
        <Button type="button" $variant="danger"
          onClick={() => onChange({ ...value, covenants: value.covenants.filter((_, i) => i !== index) })}>
          <Trash2 size={13} /> Remove
        </Button>
      </div>
      <Fields>
        <Field>
          <label htmlFor={`covenant-name-${index}`}>Covenant name</label>
          <Input id={`covenant-name-${index}`} value={covenant.covenantName} autoComplete="off"
            placeholder="Maximum 60+ day delinquency"
            onChange={event => update(index, { covenantName: event.target.value })} />
        </Field>
        <Field>
          <label htmlFor={`covenant-key-${index}`}>Key</label>
          <Input id={`covenant-key-${index}`} value={covenant.covenantKey} autoComplete="off"
            placeholder="MAX_DELINQUENCY"
            onChange={event => update(index, { covenantKey: event.target.value })} />
          <span className="hint">A short stable identifier used in test results and reports.</span>
        </Field>
        <Field>
          <label htmlFor={`covenant-formula-${index}`}>What the platform computes</label>
          <select id={`covenant-formula-${index}`} value={covenant.formulaExpression}
            onChange={event => update(index, { formulaExpression: event.target.value })}>
            <option value="">Select…</option>
            {COVENANT_FORMULAS.map(formula => <option key={formula} value={formula}>{formula}</option>)}
          </select>
        </Field>
        <Field>
          <label htmlFor={`covenant-direction-${index}`}>Direction</label>
          <select id={`covenant-direction-${index}`} value={covenant.thresholdType}
            onChange={event => update(index, { thresholdType: event.target.value as 'MIN' | 'MAX' })}>
            <option value="MIN">Must stay at or above (MIN)</option>
            <option value="MAX">Must stay at or below (MAX)</option>
          </select>
        </Field>
        <Field>
          <label htmlFor={`covenant-threshold-${index}`}>Threshold</label>
          <Input id={`covenant-threshold-${index}`} type="number" step="any" value={covenant.thresholdValue}
            onChange={event => update(index, { thresholdValue: event.target.value })} />
        </Field>
        <Field>
          <label htmlFor={`covenant-frequency-${index}`}>Tested</label>
          <select id={`covenant-frequency-${index}`} value={covenant.reportingFrequency}
            onChange={event => update(index, { reportingFrequency: event.target.value })}>
            <option value="">Select…</option>
            {REPORTING_FREQUENCIES.map(frequency => <option key={frequency} value={frequency}>{frequency}</option>)}
          </select>
        </Field>
        <Field>
          <label htmlFor={`covenant-clause-${index}`}>Clause</label>
          <Input id={`covenant-clause-${index}`} value={covenant.sourceClause} autoComplete="off"
            placeholder="Section 7.02(b)"
            onChange={event => update(index, { sourceClause: event.target.value })} />
        </Field>
      </Fields>
    </Repeated>)}
  </Card>;
};
